// Full-screen review mode for press-kit photography.
//
// One mode with a density control rather than two modes with a toggle: the
// single-image view is simply density 1. That keeps one mental model and makes
// stepping between "look closely at this frame" and "compare the whole take" a
// continuous move instead of a context switch.
//
// The interaction that makes culling fast is separating FOCUS from SELECTION:
//
//   focus     one image, follows the arrow keys AND the mouse. Rating keys act
//             on it, so you sweep the pointer across a grid tapping 4/2/5 and
//             never click at all — the trick every dedicated culling tool uses.
//   selection a set, built by clicking or Space, for batch hashtagging. When a
//             selection exists the rating keys act on all of it instead, so
//             there is one rule rather than a mode.
//
// PDFs are not part of the pool; CommsPage keeps sending those to the plain
// Lightbox. This mode is for judging photographs.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { GOLD } from './commsUi';
import {
  StarPicker, TagAssetsModal, SHORTLIST_MIN_RANK, callEdgeFunction, withListingSlot, useFileUrl,
  listFolder, isPreviewable, isPdf, BRIO_PRESS_BASE, compareByName, slugTag, tagsForAsset,
} from './pressAssets';
import { ThrottledImg } from './thumbLoader';
import { RenameAssetsModal } from './renameAssets';
import { ShareAssetsModal } from './pressShares';
import { filterVariantFolders, VERSION_PREF_AUTO } from './assetVariants';

const DENSITIES = [1, 2, 3, 4, 5];
const DENSITY_KEY = 'comms_review_density';
const SCOPE_KEY = 'comms_review_scope';
const NAMES_KEY = 'comms_review_names';

const SCOPES = [
  { key: 'folder', label: 'Folder' },
  { key: 'section', label: 'Section' },
];

// section → photographer → image-version folder. Bounded so a surprise in the
// tree cannot fan out into an unbounded crawl.
const MAX_WALK_DEPTH = 3;

// Below this many across the thumbnail tier is too soft to judge on, so the
// cell shows the real file instead. At 3+ across the large tier is sharp enough
// and far lighter than a screen full of full-resolution JPEGs.
const FULL_RES_MAX_DENSITY = 2;
// Asked of list-press-folder-contents when the mode opens; cached per
// (content_hash, size_tag) so a folder pays for it once, ever.
const REVIEW_THUMB_SIZE = 'w1024h768';

const FILTERS = [
  { key: 'all', label: 'All' },
  // The query that actually matters mid-shoot: what have I not judged yet.
  // It also makes the pass self-terminating — you are done when it is empty.
  { key: 'unranked', label: 'Unranked' },
  { key: 'shortlist', label: `${SHORTLIST_MIN_RANK}★+` },
];

function readDensity() {
  try {
    const n = Number(localStorage.getItem(DENSITY_KEY));
    if (DENSITIES.includes(n)) return n;
  } catch { /* private browsing — fall back to the default */ }
  return 3;
}

function writeDensity(n) {
  try { localStorage.setItem(DENSITY_KEY, String(n)); } catch { /* nothing to do */ }
}

function readScope() {
  try {
    const v = localStorage.getItem(SCOPE_KEY);
    if (SCOPES.some(o => o.key === v)) return v;
  } catch { /* private browsing — fall back to the default */ }
  return 'folder';
}

function writeScope(v) {
  try { localStorage.setItem(SCOPE_KEY, v); } catch { /* nothing to do */ }
}

function readShowNames() {
  try {
    const v = localStorage.getItem(NAMES_KEY);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch { /* private browsing — fall back to the default */ }
  return true;
}

function writeShowNames(on) {
  try { localStorage.setItem(NAMES_KEY, on ? '1' : '0'); } catch { /* nothing to do */ }
}

/**
 * Filenames differ at the END — DSC_0142 / DSC_0143 — so a label clipped with
 * a trailing ellipsis hides the only part that tells two frames apart. Keep
 * both ends and drop the middle instead.
 */
export function shortName(name, max = 32) {
  const s = name || '';
  if (s.length <= max) return s;
  const head = Math.ceil((max - 1) / 2);
  const tail = max - 1 - head;
  return `${s.slice(0, head)}…${s.slice(s.length - tail)}`;
}

// ── SEARCH ────────────────────────────────────────────────────────────────
//
// Press-kit filenames carry their structure in separators — AR_Fernandes_ext-01
// .jpg, DSC_0143.tif — and nobody remembers which separator was used where. So
// the query is normalised exactly like the haystack: case folded, separators
// flattened to spaces. "fernandes ext" then finds AR_Fernandes_ext-01.jpg
// without the typist guessing at underscores.
//
// Tokens are ANDed and order-free, because what you remember about a file is
// two fragments, not their sequence.

/** Case, separators and runs of whitespace folded away. */
export function normalizeForSearch(s) {
  return (s || '').toLowerCase().replace(/[_\-./\\]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * What a query is matched against: the path below the press base, not just the
 * file name. Under section scope that means typing a photographer, a section or
 * an image-version folder narrows to it — those folder names are the only
 * description most of these files have.
 */
export function searchHaystack(entry) {
  const p = entry?.path || entry?.name || '';
  const rel = p.startsWith(`${BRIO_PRESS_BASE}/`) ? p.slice(BRIO_PRESS_BASE.length + 1) : p;
  return normalizeForSearch(rel);
}

/**
 * A predicate over (entry, tags), or null for an empty query. A token starting
 * with # is matched against the asset's hashtags rather than its path, so one
 * box searches both of the things an asset is known by.
 */
export function makeSearchMatcher(query) {
  const raw = (query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  const tagTokens = raw.filter(t => t.startsWith('#')).map(slugTag).filter(Boolean);
  const textTokens = raw.filter(t => !t.startsWith('#')).map(normalizeForSearch).filter(Boolean);
  if (!tagTokens.length && !textTokens.length) return null;
  return (entry, tags) => {
    const hay = searchHaystack(entry);
    if (!textTokens.every(t => hay.includes(t))) return false;
    if (!tagTokens.length) return true;
    const have = (tags || []).map(slugTag);
    return tagTokens.every(t => have.some(h => h.includes(t)));
  };
}

/**
 * The section a press-kit folder sits under:
 * {base}/{project}/{section}/... → {base}/{project}/{section}.
 * Returns null for a path that is already at or above section level, where
 * section scope would mean the same thing as folder scope.
 */
export function sectionPathOf(folderPath) {
  if (!folderPath?.startsWith(`${BRIO_PRESS_BASE}/`)) return null;
  const parts = folderPath.slice(BRIO_PRESS_BASE.length + 1).split('/');
  if (parts.length < 3) return null;
  return `${BRIO_PRESS_BASE}/${parts[0]}/${parts[1]}`;
}

/**
 * Every previewable file under a folder, walking into subfolders.
 *
 * Image-version siblings (jpeg-highres / tiff / …) are collapsed by the same
 * rule the folder browser uses, so section scope shows each photograph once at
 * the chosen version rather than three times at three resolutions.
 */
function toPoolEntries(entries, folderPath) {
  return entries
    .filter(e => !e.is_folder && isPreviewable(e) && !isPdf(e.name))
    .map(e => ({
      url: '', path: `${folderPath}/${e.name}`, name: e.name,
      isPdf: false, isVideo: !!e.is_video,
      fileId: e.file_id || null, thumbUrl: e.thumb_url || null,
      clientModified: e.client_modified || null,
      folderPath,
    }));
}

async function walkFolder(folderPath, versionPref, depth = 0) {
  const entries = await listFolder(folderPath);
  const files = toPoolEntries(entries, folderPath);
  if (depth >= MAX_WALK_DEPTH) return files;
  const folders = filterVariantFolders(entries.filter(e => e.is_folder), f => f.name, versionPref);
  const nested = await Promise.all(
    folders.map(f => walkFolder(`${folderPath}/${f.name}`, versionPref, depth + 1).catch(() => [])),
  );
  return [...files, ...nested.flat()];
}

/**
 * The images under review. Folder scope reuses the listing the grid already
 * had, so switching in is instant; section scope walks the tree, which is one
 * listing per subfolder the first time and cache hits afterwards.
 *
 * `deep` is how a folder opened from its name in the kit behaves: nothing was
 * handed down, so the folder is walked the way a section is, subfolders
 * included and image versions collapsed to the chosen one.
 *
 * `rev` is bumped after a rename. At that point the entries handed down as a
 * prop describe names that no longer exist — and nothing upstream re-sends
 * them — so the folder is re-read here too, not just the section.
 */
function usePool(entries, folderPath, scope, versionPref, rev = 0, deep = false) {
  const propPool = useMemo(() => (entries || []).filter(e => !e.isPdf), [entries]);
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const sectionPath = scope === 'section' ? sectionPathOf(folderPath) : null;
  const mustFetch = !!sectionPath || rev > 0 || deep;

  useEffect(() => {
    if (!mustFetch) { setFetched(null); return; }
    let cancelled = false;
    setLoading(true);
    const load = sectionPath
      ? walkFolder(sectionPath, versionPref)
      : deep
        ? walkFolder(folderPath, versionPref)
        : listFolder(folderPath).then(es => toPoolEntries(es, folderPath));
    load
      .then(found => { if (!cancelled) setFetched(found.sort(compareByName)); })
      .catch(() => { if (!cancelled) setFetched([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mustFetch, sectionPath, folderPath, versionPref, rev, deep]);

  if (!mustFetch) return { pool: propPool, loading: false };
  return { pool: fetched ?? propPool, loading: loading && !fetched };
}

const mono = "'IBM Plex Mono', monospace";

/**
 * Larger thumbnails for the whole folder, fetched once when the mode opens.
 * Re-listing the folder is one Dropbox call and reuses the tested caching path
 * in the edge function; asking for links again is skipped because the mode
 * resolves those per image on demand. Falls back silently to the small tier —
 * a soft grid beats an empty one.
 */
function useLargeThumbs(pool, active) {
  const [byPath, setByPath] = useState({});
  // Keyed by full path, not name: the same camera filename turns up in two
  // photographers' folders, and under section scope both are in the pool.
  const folderPaths = useMemo(
    () => [...new Set((pool || []).map(e => e.path.slice(0, e.path.lastIndexOf('/'))))].sort(),
    [pool],
  );
  const key = folderPaths.join('|');

  useEffect(() => {
    if (!active || !folderPaths.length) return;
    let cancelled = false;
    // Through the shared listing gate: a section can hold a dozen folders,
    // and each of these calls generates and uploads the large tier for every
    // file it has not seen yet.
    Promise.all(folderPaths.map(folderPath =>
      withListingSlot(() => callEdgeFunction('list-press-folder-contents', {
        folderPath, thumbSize: REVIEW_THUMB_SIZE, previewLinks: false,
      }))
        .then(json => ({ folderPath, entries: json.entries || [] }))
        .catch(() => ({ folderPath, entries: [] })),
    )).then(results => {
      if (cancelled) return;
      const m = {};
      results.forEach(({ folderPath, entries }) => {
        entries.forEach(e => { if (e.thumb_url) m[`${folderPath}/${e.name}`] = e.thumb_url; });
      });
      setByPath(m);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, active]);

  return byPath;
}

/** The real file, shown once the grid is down to one or two across. */
function FullResCell({ entry }) {
  const { url, error } = useFileUrl(entry);
  const style = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' };
  if (entry.isVideo) {
    return url
      ? <video src={url} controls style={style} />
      : <span style={{ fontFamily: mono, fontSize: 11, color: 'rgba(255,255,255,.5)' }}>Loading…</span>;
  }
  if (!url) {
    return <span style={{ fontFamily: mono, fontSize: 11, color: 'rgba(255,255,255,.5)' }}>{error || 'Loading…'}</span>;
  }
  return <img src={url} alt={entry.name} style={style} />;
}

function ThumbCell({ entry, src }) {
  if (!src) {
    return (
      <span style={{ fontFamily: mono, fontSize: 10, color: 'rgba(255,255,255,.45)', textAlign: 'center', padding: 8, wordBreak: 'break-all' }}>
        {entry.name}
      </span>
    );
  }
  return (
    <ThrottledImg src={src} alt={entry.name}
      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
      // The cell centres its content, so the box holding the tile's space has
      // to fill it for the near-viewport check to see it.
      placeholderStyle={{ width: '100%', height: '100%' }}
      fallback={
        <span style={{ fontFamily: mono, fontSize: 10, color: 'rgba(255,255,255,.45)', textAlign: 'center', padding: 8, wordBreak: 'break-all' }}>
          {entry.name}
        </span>
      } />
  );
}

// `deep` opens the mode on a folder rather than on a picture: the pool is
// everything under `folderPath`, walked on open, and `entries` may be empty.
export function ReviewMode({
  entries, index, folderPath, label, onClose, rankView, tagsByAsset, onTagsSaved, showToast,
  versionPref = VERSION_PREF_AUTO, canRename = false, canShare = false, kitSchema = [], projectCode, project,
  onRenamed, onShared, deep = false,
}) {
  const [density, setDensity] = useState(readDensity);
  const [scope, setScope] = useState(readScope);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showNames, setShowNames] = useState(readShowNames);
  const [focusPath, setFocusPath] = useState(entries?.[index ?? 0]?.path || null);
  const [selected, setSelected] = useState({}); // path -> entry
  const [tagOpen, setTagOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [poolRev, setPoolRev] = useState(0);
  // A rename changes every path in the batch, so focus — which is tracked by
  // path — has to be re-found by file id, which a rename does not change.
  const [refocusFileId, setRefocusFileId] = useState(null);
  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimer = useRef(null);
  const searchRef = useRef(null);

  const rankOf = useCallback(
    e => (e?.fileId && rankView?.byId?.[e.fileId]) || 0,
    [rankView],
  );

  const { pool, loading: poolLoading } = usePool(entries, folderPath, scope, versionPref, poolRev, deep);
  // The rank filter and the search compose: "unranked" plus "fernandes" is the
  // question you actually have mid-pass, not two separate ones.
  const matches = useMemo(() => makeSearchMatcher(query), [query]);
  const visible = useMemo(() => {
    let list = pool;
    if (filter === 'unranked') list = list.filter(e => rankOf(e) === 0);
    else if (filter === 'shortlist') list = list.filter(e => rankOf(e) >= SHORTLIST_MIN_RANK);
    if (matches) list = list.filter(e => matches(e, tagsForAsset(tagsByAsset, e.fileId, e.path)));
    return list;
  }, [pool, filter, rankOf, matches, tagsByAsset]);

  // Focus is tracked by path, not index, so filtering or a rank change that
  // reorders what is on screen leaves it pointing at the same photograph.
  const focusIdx = Math.max(0, visible.findIndex(e => e.path === focusPath));
  const focused = visible[focusIdx] || null;
  useEffect(() => {
    if (visible.length && !visible.some(e => e.path === focusPath)) setFocusPath(visible[0].path);
  }, [visible, focusPath]);

  useEffect(() => {
    if (!refocusFileId) return;
    const found = pool.find(e => e.fileId === refocusFileId);
    if (found) { setFocusPath(found.path); setRefocusFileId(null); }
  }, [pool, refocusFileId]);

  const largeThumbs = useLargeThumbs(pool, density > FULL_RES_MAX_DENSITY);

  // Arrowing past the fold has to bring the cell with it, or the focus ring
  // walks off screen and the rating keys act on something you cannot see.
  const scrollerRef = useRef(null);
  useEffect(() => {
    if (!focusPath || !scrollerRef.current) return;
    const cell = scrollerRef.current.querySelector(`[data-review-path="${CSS.escape(focusPath)}"]`);
    cell?.scrollIntoView({ block: 'nearest' });
  }, [focusPath, density]);

  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const toggleSelect = useCallback(entry => {
    setSelected(s => {
      const next = { ...s };
      if (next[entry.path]) delete next[entry.path]; else next[entry.path] = entry;
      return next;
    });
  }, []);

  // ── Finder's selection grammar ────────────────────────────────────────────
  // Plain click selects just that image, Cmd/Ctrl+click adds or removes one,
  // Shift+click takes everything between the last click and this one.
  //
  // The anchor is the last CLICKED tile and deliberately not the focused one.
  // Focus follows the mouse in this grid, so anchoring on it would make a
  // range depend on the path the cursor happened to take to reach the tile.
  const [anchorPath, setAnchorPath] = useState(null);

  const selectRange = useCallback((toPath, additive) => {
    const from = visible.findIndex(e => e.path === anchorPath);
    const to = visible.findIndex(e => e.path === toPath);
    if (to < 0) return;
    // No anchor yet (first click, or it was filtered out of view) — the range
    // is just the one tile, which is also what Finder does.
    const [lo, hi] = from < 0 ? [to, to] : [Math.min(from, to), Math.max(from, to)];
    setSelected(s => {
      const next = additive ? { ...s } : {};
      for (let i = lo; i <= hi; i++) next[visible[i].path] = visible[i];
      return next;
    });
  }, [visible, anchorPath]);

  const onCellClick = useCallback((ev, entry) => {
    const additive = ev.metaKey || ev.ctrlKey;
    if (ev.shiftKey) {
      // The anchor stays where it was, so a run of Shift+clicks re-draws the
      // range from the same start rather than creeping along with each click.
      selectRange(entry.path, additive);
      return;
    }
    setAnchorPath(entry.path);
    if (additive) { toggleSelect(entry); return; }
    setSelected({ [entry.path]: entry });
  }, [selectRange, toggleSelect]);

  const selectAllVisible = useCallback(() => {
    setSelected(Object.fromEntries(visible.map(e => [e.path, e])));
  }, [visible]);

  // Rating targets the selection when there is one, otherwise the focused
  // image. One rule, so the keys never mean something different than they look.
  const rate = rankView?.onRate;
  const rateTargets = useCallback(() => (selectedList.length ? selectedList : focused ? [focused] : []), [selectedList, focused]);

  // The grid density to come back to when Escape leaves a zoomed image.
  // Remembered rather than re-read, because changeDensity has already written
  // the zoom to storage by then.
  const gridDensity = useRef(readDensity() === 1 ? 3 : readDensity());
  const changeScope = useCallback(v => { setScope(v); writeScope(v); }, []);
  const changeDensity = useCallback(n => {
    setDensity(prev => { if (prev > 1) gridDensity.current = prev; return n; });
    writeDensity(n);
  }, []);

  const toggleNames = useCallback(() => {
    setShowNames(prev => { writeShowNames(!prev); return !prev; });
  }, []);

  // Closing the box clears the query: a filter you cannot see is a filter you
  // will not remember is on.
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => { setSearchOpen(false); setQuery(''); }, []);
  useEffect(() => { if (searchOpen) searchRef.current?.focus(); }, [searchOpen]);

  const revealChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setChromeVisible(false), 2200);
  }, []);
  useEffect(() => {
    revealChrome();
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [revealChrome]);

  useEffect(() => {
    const onKey = e => {
      if (tagOpen || renameOpen || shareOpen) return;   // the modal owns the keyboard

      // While the search box has focus it owns the keyboard — otherwise typing
      // a filename would rate and re-size instead of searching.
      const el = e.target;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

      // Select-all, as in Finder. Read before the modifier bail-out below —
      // that exists to leave browser shortcuts alone, and this is the one
      // shortcut we deliberately take. Never while a text field has the keys,
      // where Cmd+A still has to mean select-all-text.
      if (!typing && (e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        revealChrome();
        selectAllVisible();
        return;
      }
      if (e.altKey || e.metaKey || e.ctrlKey) return;   // leave browser shortcuts alone

      if (typing) {
        if (e.key === 'Escape') { e.preventDefault(); closeSearch(); }
        // Enter hands the keys back while keeping the query, so you can filter
        // and then rate the result without reaching for the mouse.
        if (e.key === 'Enter') { e.preventDefault(); el.blur(); }
        return;
      }
      revealChrome();

      // Shift+1–5 sets density; the plain digits are rating, which is the far
      // more frequent action and matches every other culling tool.
      //
      // Matched on e.code rather than e.key: with Shift held the browser
      // reports the shifted character ("!" for the 1 key), so testing e.key
      // here would never fire.
      if (e.shiftKey && /^Digit[1-5]$/.test(e.code)) {
        e.preventDefault();
        changeDensity(Number(e.code.slice(5)));
        return;
      }
      // Before the Shift bail-out: on plenty of layouts "/" is a shifted key.
      if (e.key === '/') { e.preventDefault(); openSearch(); return; }
      if (e.shiftKey) return;

      if (/^[0-5]$/.test(e.key)) {
        const targets = rateTargets();
        if (!rate || !targets.length) return;
        e.preventDefault();
        targets.forEach(t => rate(t, Number(e.key)));
        return;
      }
      const step = d => {
        if (!visible.length) return;
        const next = Math.min(visible.length - 1, Math.max(0, focusIdx + d));
        setFocusPath(visible[next].path);
      };
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          // Steps out one level: a zoomed image returns to the grid it came
          // from rather than closing the whole mode.
          if (density === 1) changeDensity(gridDensity.current);
          else onClose();
          break;
        case 'ArrowLeft':  e.preventDefault(); step(-1); break;
        case 'ArrowRight': e.preventDefault(); step(1); break;
        case 'ArrowUp':    e.preventDefault(); step(-density); break;
        case 'ArrowDown':  e.preventDefault(); step(density); break;
        case ' ':
          e.preventDefault();
          if (focused) { toggleSelect(focused); setAnchorPath(focused.path); }
          break;
        case 't': case 'T':
          e.preventDefault();
          if (selectedList.length || focused) setTagOpen(true);
          break;
        case 'r': case 'R':
          if (!canRename) break;
          e.preventDefault();
          if (selectedList.length || focused) setRenameOpen(true);
          break;
        case 's': case 'S':
          if (!canShare) break;
          e.preventDefault();
          if (selectedList.length || focused) setShareOpen(true);
          break;
        case 'n': case 'N':
          e.preventDefault();
          toggleNames();
          break;
        case 'f': case 'F': {
          e.preventDefault();
          const i = FILTERS.findIndex(f => f.key === filter);
          setFilter(FILTERS[(i + 1) % FILTERS.length].key);
          break;
        }
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, focusIdx, focused, density, filter, rate, rateTargets, selectedList,
      tagOpen, renameOpen, shareOpen, canRename, canShare, toggleSelect, changeDensity, revealChrome, onClose,
      openSearch, closeSearch, toggleNames, selectAllVisible]);

  // Opened from a picture, an empty pool means there is nothing to show at
  // all. Opened from a folder, the mode stays up to say the folder is empty.
  if (!pool.length && !poolLoading && !deep) return null;

  // The project code travels with the asset: comms.asset_tags stores it, and
  // the pitch deck builder matches on it. Review mode is where a whole shoot
  // gets tagged, and leaving it off wrote 249 rows that no deck could see.
  const tagAssets = (selectedList.length ? selectedList : focused ? [focused] : [])
    .map(a => ({ ...a, thumbUrl: a.thumbUrl || null, projectCode: a.projectCode || projectCode || null }));

  // The chrome must not fade out from under a search box you are typing into.
  const chromeShown = chromeVisible || searchOpen;
  const chromeStyle = {
    opacity: chromeShown ? 1 : 0,
    transition: 'opacity .25s',
    pointerEvents: chromeShown ? 'auto' : 'none',
  };
  const btn = (active, extra = {}) => ({
    fontFamily: mono, fontSize: 11, lineHeight: 1, cursor: 'pointer', borderRadius: 3,
    padding: '5px 9px', border: `1px solid ${active ? GOLD : 'rgba(255,255,255,.25)'}`,
    background: active ? GOLD : 'transparent', color: active ? '#fff' : 'rgba(255,255,255,.75)',
    ...extra,
  });

  return (
    <div
      onMouseMove={revealChrome}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#0B0B0A', display: 'flex', flexDirection: 'column' }}
    >
      {/* ── top bar ─────────────────────────────────────────────────── */}
      <div style={{ ...chromeStyle, position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '12px 16px', background: 'linear-gradient(rgba(0,0,0,.75), rgba(0,0,0,0))' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: mono, fontSize: 11, color: 'rgba(255,255,255,.85)' }}>{label}</div>
          {/* The focused file's name in full — the tile caption is truncated,
              and at density 1 there is no caption at all. */}
          {focused && (
            <div title={focused.path}
              style={{ fontFamily: mono, fontSize: 11, color: GOLD, marginTop: 2, wordBreak: 'break-all' }}>
              {focused.name}
            </div>
          )}
          <div style={{ fontFamily: mono, fontSize: 10, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>
            {visible.length ? focusIdx + 1 : 0} / {visible.length}
            {visible.length !== pool.length ? ` of ${pool.length}` : ''}
            {' · '}{pool.filter(e => rankOf(e) >= SHORTLIST_MIN_RANK).length} shortlisted
            {matches ? ` · matching “${query.trim()}”` : ''}
            {selectedList.length ? ` · ${selectedList.length} selected` : ''}
            {poolLoading ? (scope === 'section' ? ' · loading section…' : ' · loading folder…') : ''}
          </div>
        </div>
        {/* Wraps rather than overflowing: the search box makes this row long. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10, rowGap: 6 }}>
          {searchOpen ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Search size={12} color="rgba(255,255,255,.55)" />
              <input
                ref={searchRef}
                value={query}
                onChange={ev => setQuery(ev.target.value)}
                placeholder="name, folder or #tag"
                aria-label="Search file names"
                style={{
                  fontFamily: mono, fontSize: 11, width: 190, padding: '4px 7px', borderRadius: 3,
                  border: `1px solid ${query ? GOLD : 'rgba(255,255,255,.25)'}`,
                  background: 'rgba(0,0,0,.45)', color: '#fff', outline: 'none',
                }}
              />
              <button onClick={closeSearch} title="Clear the search — Esc"
                style={btn(false, { display: 'flex', padding: '5px 6px' })} aria-label="Clear the search">
                <X size={12} />
              </button>
            </div>
          ) : (
            <button onClick={openSearch} title="Search file names — /"
              style={btn(false, { display: 'flex', padding: '5px 7px' })} aria-label="Search file names">
              <Search size={13} />
            </button>
          )}
          <button onClick={toggleNames} style={btn(showNames)} title="Show file names on the tiles — N">
            Names
          </button>
          {sectionPathOf(folderPath) && (
            <div style={{ display: 'flex', gap: 3 }} title="Review this folder, or every photograph in the section">
              {SCOPES.map(o => (
                <button key={o.key} onClick={() => changeScope(o.key)} style={btn(scope === o.key)}>{o.label}</button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 3 }} title="Images across — Shift+1…5">
            {DENSITIES.map(n => (
              <button key={n} onClick={() => changeDensity(n)} style={btn(density === n, { minWidth: 26 })}>{n}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 3 }} title="Filter — F">
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={btn(filter === f.key)}>{f.label}</button>
            ))}
          </div>
          <button onClick={onClose} title="Close — Esc"
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 26, lineHeight: 1, cursor: 'pointer', opacity: 0.7 }}>×</button>
        </div>
      </div>

      {/* ── the images ──────────────────────────────────────────────── */}
      <div ref={scrollerRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: density === 1 ? 0 : 6 }}>
        {visible.length === 0 ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: mono, fontSize: 12, color: 'rgba(255,255,255,.5)' }}>
            {!pool.length
              ? (poolLoading ? 'Loading…' : 'No images in this folder.')
              : matches
                ? `No file here matches “${query.trim()}”.`
                : filter === 'unranked' ? 'Everything here has been ranked.' : 'Nothing matches this filter.'}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${density}, 1fr)`,
            gap: density === 1 ? 0 : 6,
            alignContent: density === 1 ? 'center' : 'start',
            minHeight: density === 1 ? '100%' : undefined,
          }}>
            {visible.map(e => {
              const isFocus = e.path === focusPath;
              const isSel = !!selected[e.path];
              const rank = rankOf(e);
              const tags = tagsForAsset(tagsByAsset, e.fileId, e.path);
              return (
                <div key={e.path}
                  onMouseEnter={() => setFocusPath(e.path)}
                  onClick={ev => onCellClick(ev, e)}
                  onDoubleClick={() => { setFocusPath(e.path); changeDensity(1); }}
                  title={e.name}
                  data-review-path={e.path}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    // Without this, Shift+click drags a text highlight across the grid.
                    userSelect: 'none',
                    ...(density === 1 ? { height: 'calc(100vh - 12px)' } : { aspectRatio: '3 / 2' }),
                    background: '#131312', borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
                    outline: isSel ? `2px solid ${GOLD}` : isFocus ? '2px solid rgba(255,255,255,.55)' : 'none',
                    outlineOffset: -2,
                  }}>
                  {density <= FULL_RES_MAX_DENSITY
                    ? <FullResCell entry={e} />
                    : <ThumbCell entry={e} src={largeThumbs[e.path] || e.thumbUrl} />}

                  {/* Skipped at density 1, where the cell is the whole screen
                      and the top bar carries the name instead. */}
                  {showNames && density > 1 && (
                    <span
                      style={{
                        position: 'absolute', top: 0, left: 0, right: 0,
                        fontFamily: mono, fontSize: density >= 4 ? 8 : 9, lineHeight: 1.3,
                        // Room on the right for the selection tick.
                        color: 'rgba(255,255,255,.9)', padding: '3px 24px 10px 5px',
                        background: 'linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,0))',
                        whiteSpace: 'nowrap', overflow: 'hidden', pointerEvents: 'none',
                      }}>
                      {shortName(e.name, density >= 4 ? 20 : 34)}
                    </span>
                  )}

                  {rank > 0 && (
                    <span style={{ position: 'absolute', left: 6, bottom: 6, fontSize: 11, letterSpacing: '0.5px', color: GOLD, background: 'rgba(0,0,0,.55)', borderRadius: 2, padding: '1px 5px' }}>
                      {'★'.repeat(rank)}
                    </span>
                  )}
                  {tags?.length > 0 && (
                    <span style={{ position: 'absolute', right: 6, bottom: 6, maxWidth: 'calc(100% - 60px)', fontFamily: mono, fontSize: 9, color: 'rgba(255,255,255,.8)', background: 'rgba(0,0,0,.55)', borderRadius: 2, padding: '1px 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tags.map(t => `#${t}`).join(' ')}
                    </span>
                  )}
                  {isSel && (
                    <span style={{ position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%', background: GOLD, color: '#fff', fontSize: 11, lineHeight: '16px', textAlign: 'center', fontFamily: mono }}>✓</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── bottom bar ──────────────────────────────────────────────── */}
      <div style={{ ...chromeStyle, position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '14px 16px', background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,.8))' }}>
        {focused && (
          <div onClick={ev => ev.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.45)', borderRadius: 4, padding: '5px 10px' }}>
            <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>
              {selectedList.length ? `Rank ${selectedList.length}` : 'Rank'}
            </span>
            {focused.fileId
              ? <StarPicker onDark size={18}
                  value={selectedList.length ? 0 : rankOf(focused)}
                  onChange={r => rateTargets().forEach(t => rate?.(t, r))} />
              : <span style={{ fontFamily: mono, fontSize: 10, color: 'rgba(255,255,255,.55)' }}>reload the folder first</span>}
          </div>
        )}
        <button onClick={() => setTagOpen(true)} style={btn(false, { padding: '7px 12px' })} title="Hashtags — T">
          Hashtags{selectedList.length ? ` (${selectedList.length})` : ''}
        </button>
        {canRename && (
          <button onClick={() => setRenameOpen(true)} style={btn(false, { padding: '7px 12px' })} title="Rename — R">
            Rename{selectedList.length ? ` (${selectedList.length})` : ''}
          </button>
        )}
        {canShare && (
          <button onClick={() => setShareOpen(true)} style={btn(false, { padding: '7px 12px' })} title="Share as one Dropbox link — S">
            Share{selectedList.length ? ` (${selectedList.length})` : ''}
          </button>
        )}
        {selectedList.length > 0 && (
          <button onClick={() => setSelected({})} style={btn(false, { padding: '7px 12px' })}>Clear selection</button>
        )}
        <span style={{ fontFamily: mono, fontSize: 9, color: 'rgba(255,255,255,.4)' }}>
          1–5 rank · Shift+1–5 size · Shift-click range · ⌘-click add · ⌘A all · Space toggle · T tags{canRename ? ' · R rename' : ''}{canShare ? ' · S share' : ''} · F filter · / search · N names · Esc back
        </span>
      </div>

      {renameOpen && tagAssets.length > 0 && (
        <RenameAssetsModal
          assets={tagAssets}
          kitSchema={kitSchema}
          ranksById={rankView?.byId || {}}
          projectCode={projectCode}
          project={project}
          onClose={() => setRenameOpen(false)}
          onDone={() => {
            // Keep the eye on the same photograph across the rename, drop the
            // selection (its paths are dead), and re-read the folder.
            setRefocusFileId(focused?.fileId || null);
            setSelected({});
            setRenameOpen(false);
            setPoolRev(v => v + 1);
            onRenamed?.();
          }}
          showToast={showToast}
        />
      )}

      {shareOpen && tagAssets.length > 0 && (
        <ShareAssetsModal
          assets={tagAssets}
          projectCode={projectCode}
          onClose={() => setShareOpen(false)}
          onDone={() => { setSelected({}); onShared?.(); }}
          showToast={showToast}
        />
      )}

      {tagOpen && tagAssets.length > 0 && (
        <TagAssetsModal
          assets={tagAssets}
          onClose={() => setTagOpen(false)}
          onDone={() => { setTagOpen(false); onTagsSaved?.(); showToast?.('Hashtags saved'); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}
